import React, { useEffect, useRef } from 'react'
import { useMicrophoneLevel } from 'hooks/useMicrophoneLevel'
import styles from './styles.module.css'

export interface MicrophoneWaveformProps {
  /** Must only be enabled during user-initiated recording. */
  active: boolean
}

/** Local audio visualisation. Failure leaves quiet dots; speech recognition remains independent. */
export const MicrophoneWaveform: React.FC<MicrophoneWaveformProps> = ({ active }) => {
  const { level, status } = useMicrophoneLevel(active)
  const canvas = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const node = canvas.current
    if (!node) return
    const ctx = node.getContext('2d')
    if (!ctx) return
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)')
    const history: number[] = []
    let frame = 0
    let lastSample = 0
    let width = 0
    let height = 28
    let rtl = false
    let color = '#8c8c84'
    const interval = 80
    const spacing = 6

    const resize = () => {
      width = node.clientWidth
      height = node.clientHeight || 28
      const ratio = window.devicePixelRatio || 1
      node.width = Math.round(width * ratio)
      node.height = Math.round(height * ratio)
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
      const style = window.getComputedStyle(node)
      rtl = style.direction === 'rtl'
      color = style.color
      if (status !== 'active') draw(0)
    }
    const draw = (time: number) => {
      if (time - lastSample >= interval) {
        history.unshift(status === 'active' ? level.current : 0)
        history.length = Math.min(history.length, Math.ceil(width / spacing) + 2)
        lastSample = time
      }
      ctx.clearRect(0, 0, width, height)
      ctx.strokeStyle = color
      ctx.lineWidth = 2.5
      ctx.lineCap = 'round'
      // Reduced motion keeps positions fixed; only the current level changes bar heights.
      const offset = motion.matches ? 0 : Math.min(1, (time - lastSample) / interval) * spacing
      for (let i = 0; i < Math.ceil(width / spacing) + 1; i++) {
        const amplitude = motion.matches ? (status === 'active' ? level.current : 0) : history[i] || 0
        const x = rtl ? width - (i * spacing + offset) : i * spacing + offset
        const barHeight = 2.5 + amplitude * Math.max(0, height - 5)
        ctx.beginPath()
        ctx.moveTo(x, (height - barHeight) / 2 + 1.25)
        ctx.lineTo(x, (height + barHeight) / 2 - 1.25 + 0.01)
        ctx.stroke()
      }
      if (status === 'active') frame = window.requestAnimationFrame(draw)
    }
    resize()
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : undefined
    observer?.observe(node)
    window.addEventListener('resize', resize)
    frame = window.requestAnimationFrame(draw)
    return () => {
      window.cancelAnimationFrame(frame)
      observer?.disconnect()
      window.removeEventListener('resize', resize)
    }
  }, [level, status])

  return <canvas ref={canvas} className={styles.waveform} aria-hidden="true" data-state={status} />
}
